import { ApiError } from "@/generated-api";
import * as Sentry from "@sentry/react";

function hasName(value: unknown): value is { name: string } {
  return (
    typeof value === "object" &&
    value !== null &&
    "name" in value &&
    typeof (value as Record<string, unknown>).name === "string"
  );
}

function getDetail(error: unknown): string | undefined {
  if (typeof error === "object" && error !== null && "body" in error) {
    const body = (error as { body?: unknown }).body;
    if (
      typeof body === "object" &&
      body !== null &&
      "detail" in body &&
      typeof (body as { detail?: unknown }).detail === "string"
    ) {
      return (body as { detail: string }).detail;
    }
  }

  return undefined;
}

function skipSentry(mutationName: string, error: unknown): boolean {
  if (
    typeof error !== "object" ||
    error === null ||
    !(error instanceof ApiError)
  ) {
    return false;
  }

  const { status } = error;
  if (status === 401) return true;

  const skipConditions: Record<string, number[]> = {
    createUnverifiedUser: [400, 409],
    verifyUser: [400],
    resendOtpUnverified: [400],
    initiateLogin: [404],
    completeLogin: [400, 404],
    resendOtp: [400],
  };
  const skipStatuses = skipConditions[mutationName];
  return skipStatuses?.includes(status) ?? false;
}

export function withSentryMutation<
  TError = unknown,
  TVariables = unknown,
  TContext = unknown
>(
  handler?: (
    error: TError,
    variables: TVariables,
    context: TContext | undefined
  ) => void
) {
  return (
    error: TError,
    variables: TVariables,
    context: TContext | undefined
  ) => {
    const mutationName = hasName(context) ? context.name : "unknown";
    if (!skipSentry(mutationName, error)) {
      const apiError = error instanceof ApiError ? error : undefined;

      // Split issues by mutation + status instead of lumping every API error
      // into one group (they all share the same throw site / stack trace).
      // When the mutation name is missing (a mutation that doesn't run through
      // useNamedMutation), fall back to the endpoint — method + templated path,
      // e.g. "POST /api/verification/upload" — so unnamed errors still split by
      // route rather than collapsing into a single "unknown" group. Non-API
      // errors get no custom fingerprint and keep Sentry's stack-based grouping.
      const groupKey =
        mutationName !== "unknown"
          ? mutationName
          : apiError
            ? `${apiError.request.method} ${apiError.request.url}`
            : undefined;

      Sentry.captureException(error, {
        tags: {
          mutation: mutationName,
          response_code: apiError?.status,
          response_detail: getDetail(error),
        },
        extra: {
          error,
          variables,
          context,
        },
        // "{{ default }}" keeps Sentry's stack-based grouping as a sub-component.
        ...(groupKey
          ? {
              fingerprint: [
                "{{ default }}",
                groupKey,
                String(apiError?.status ?? "unknown"),
              ],
            }
          : {}),
      });
    }

    handler?.(error, variables, context);
  };
}
